import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import {
	TacticalDimensionColumn,
	TacticalDimensionRow,
	TacticalDimensionSelectionMode,
	TacticalDimensionSortDir,
	TacticalDimensionSortState,
} from "./tactical-dimension-table.model";

const SCROLL_OBSERVER_MAX_SETUP_ATTEMPTS = 8;

@Component({
	selector: "app-tactical-dimension-table",
	templateUrl: "./tactical-dimension-table.component.html",
	styleUrls: ["./tactical-dimension-table.component.scss"],
})
export class TacticalDimensionTableComponent implements AfterViewInit, OnChanges, OnDestroy {
	@Input() cardTitle = "";
	@Input() icon = "";
	@Input() badgeLabel = "";
	@Input() cardExtraClass = "";
	@Input() columns: TacticalDimensionColumn[] = [];
	@Input() rows: TacticalDimensionRow[] = [];
	@Input() selectionMode: TacticalDimensionSelectionMode = "none";
	@Input() selectedKeys: string[] = [];
	@Input() loading = false;
	@Input() emptyMessage = "Sem dados para exibir.";
	@Input() showClearFilter = false;
	@Input() clearFilterAriaLabel = "Limpar filtro";
	/** Quando true, o corpo da tabela vem via ng-content (adapter Pessoas). */
	@Input() projectBody = false;
	/** Cabeçalho em coluna (título/ações + busca), no padrão do card de funcionários. */
	@Input() stackedHeader = false;
	/** Exibe slot de rodapé (paginação / totais). */
	@Input() showFooter = false;
	/** Template do rodapé (evita re-projeção aninhada de ng-content). */
	@Input() footerTemplate: TemplateRef<unknown> | null = null;
	/** Há mais linhas para carregar via scroll infinito. */
	@Input() hasMoreRows = false;
	/**
	 * Ordenação inicial (ex.: Ideal desc). Aplicada uma vez e congelada
	 * para a linha não “pular” quando as métricas mudam por filtro.
	 * Clique do usuário em uma coluna volta a ordenar ao vivo.
	 */
	@Input() defaultSort: TacticalDimensionSortState | null = {
		key: "ideal",
		dir: "desc",
	};
	@Input() sortable = true;
	/** Exibe busca no cabeçalho para listas longas (ex.: muitos cargos). */
	@Input() searchable = false;

	@Output() rowToggle = new EventEmitter<string>();
	@Output() clearFilter = new EventEmitter<void>();
	@Output() loadMore = new EventEmitter<void>();

	@ViewChild("scrollHost") scrollHost?: ElementRef<HTMLElement>;
	@ViewChild("scrollSentinel") scrollSentinel?: ElementRef<HTMLElement>;

	/** Ordenação explícita do usuário (null = usa ordem congelada / default). */
	sortState: TacticalDimensionSortState | null = null;
	searchTerm = "";

	/** Ordem estável das keys após o 1º defaultSort (ou dataset atual). */
	private lockedOrderKeys: string[] | null = null;
	private lockedOrderSignature: string | null = null;
	private scrollObserver: IntersectionObserver | undefined;
	private scrollObserverRefreshScheduled = false;
	private scrollObserverSetupAttempts = 0;
	private loadMoreInProgress = false;
	private readonly pendingTimeoutIds: ReturnType<typeof setTimeout>[] = [];

	get hasSelection(): boolean {
		return (this.selectedKeys?.length ?? 0) > 0;
	}

	get isSelectable(): boolean {
		return this.selectionMode === "single" || this.selectionMode === "multi";
	}

	get displayRows(): TacticalDimensionRow[] {
		const source = this.rows ?? [];
		if (!source.length) {
			return source;
		}

		let ordered = source;
		if (this.sortable && !this.projectBody) {
			if (this.sortState) {
				ordered = this.sortRows(source, this.sortState);
			} else if (this.defaultSort) {
				if (!this.lockedOrderKeys) {
					ordered = this.sortRows(source, this.defaultSort);
					this.lockedOrderKeys = ordered.map((row) => row.key);
				} else {
					ordered = this.applyLockedOrder(source, this.lockedOrderKeys);
				}
			}
		}

		return this.applySearchAndPinSelection(ordered);
	}

	ngAfterViewInit(): void {
		this.scheduleScrollObserverRefresh();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if ("rows" in changes) {
			const signature = this.buildRowsSignature(this.rows);
			if (signature !== this.lockedOrderSignature) {
				this.lockedOrderSignature = signature;
				this.lockedOrderKeys = null;
				// Novo conjunto de linhas (ex.: cascata): volta à ordem default congelada.
				this.sortState = null;
				this.searchTerm = "";
			}
		}

		if (changes["hasMoreRows"] || changes["loading"] || changes["rows"]) {
			this.scheduleScrollObserverRefresh();
		}
	}

	ngOnDestroy(): void {
		this.clearPendingTimeouts();
		this.disconnectScrollObserver();
	}

	trackByKey(_index: number, row: TacticalDimensionRow): string {
		return row.key || String(_index);
	}

	isSelected(row: TacticalDimensionRow): boolean {
		if (!row.key || !this.selectedKeys?.length) {
			return false;
		}

		if (this.selectedKeys.includes(row.key)) {
			return true;
		}

		const rowIds = this.splitKeyIds(row.key);
		if (!rowIds.length) {
			return false;
		}

		return this.selectedKeys.some((selected) =>
			this.splitKeyIds(selected).some((id) => rowIds.includes(id))
		);
	}

	isDimmed(row: TacticalDimensionRow): boolean {
		if (row.dimmed === true) {
			return true;
		}
		return this.hasSelection && this.isSelectable && !this.isSelected(row);
	}

	onRowClick(row: TacticalDimensionRow): void {
		if (!this.isSelectable) {
			return;
		}

		const key = (row.key || "").trim();
		if (!key) {
			return;
		}

		this.rowToggle.emit(key);
	}

	onRowKeydown(event: KeyboardEvent, row: TacticalDimensionRow): void {
		if (event.key !== "Enter" && event.key !== " ") {
			return;
		}
		event.preventDefault();
		this.onRowClick(row);
	}

	onSortClick(columnKey: string): void {
		if (!this.sortable || this.projectBody || !columnKey) {
			return;
		}

		const current = this.resolveSortState();
		if (!current || current.key !== columnKey) {
			this.sortState = { key: columnKey, dir: "desc" };
			return;
		}

		const nextDir: TacticalDimensionSortDir = current.dir === "desc" ? "asc" : "desc";
		this.sortState = { key: columnKey, dir: nextDir };
	}

	headerSortState(columnKey: string): TacticalDimensionSortDir | "none" {
		const current = this.resolveSortState();
		if (!current || current.key !== columnKey) {
			return "none";
		}
		return current.dir;
	}

	ariaSort(columnKey: string): "ascending" | "descending" | "none" {
		const state = this.headerSortState(columnKey);
		if (state === "asc") {
			return "ascending";
		}
		if (state === "desc") {
			return "descending";
		}
		return "none";
	}

	formatCell(column: TacticalDimensionColumn, row: TacticalDimensionRow): string {
		const raw = row.cells?.[column.key];
		const format = column.format || "text";

		if (format === "balance") {
			const value = typeof raw === "number" ? raw : Number(raw) || 0;
			return value > 0 ? `+${value}` : `${value}`;
		}

		if (format === "number") {
			const value = typeof raw === "number" ? raw : Number(raw);
			return Number.isFinite(value) ? String(value) : "0";
		}

		if (raw == null) {
			return "";
		}

		return String(raw);
	}

	cellToneClass(
		column: TacticalDimensionColumn,
		row: TacticalDimensionRow
	): Record<string, boolean> {
		const classes: Record<string, boolean> = {
			"tdt-cell--real": column.tone === "real",
			"tdt-cell--on-leave": column.tone === "onLeave",
			"tdt-cell--training": column.tone === "training",
			"tdt-cell--resignation": column.tone === "resignation",
			"tdt-cell--hiring": column.tone === "hiring",
			"tdt-cell--muted": column.tone === "muted",
		};

		if (column.format === "balance") {
			const raw = row.cells?.[column.key];
			const value = typeof raw === "number" ? raw : Number(raw) || 0;
			classes["tdt-cell--positive"] = value > 0;
			classes["tdt-cell--negative"] = value < 0;
			classes["tdt-cell--zero"] = value === 0;
		}

		return classes;
	}

	rowClass(row: TacticalDimensionRow): Record<string, boolean> {
		return {
			"tdt-row--clickable": this.isSelectable && !!row.key,
			"tdt-row--readonly": !this.isSelectable,
			"is-selected": this.isSelected(row),
			"tdt-row--dimmed": this.isDimmed(row),
		};
	}

	private resolveSortState(): TacticalDimensionSortState | null {
		return this.sortState ?? this.defaultSort;
	}

	private buildRowsSignature(rows: TacticalDimensionRow[] | null | undefined): string {
		return (rows ?? []).map((row) => row.key || "").join("\0");
	}

	private sortRows(
		source: TacticalDimensionRow[],
		sort: TacticalDimensionSortState
	): TacticalDimensionRow[] {
		const dir = sort.dir === "asc" ? 1 : -1;
		return [...source].sort((a, b) => {
			const left = a.cells?.[sort.key];
			const right = b.cells?.[sort.key];
			return this.compareCells(left, right) * dir;
		});
	}

	private applySearchAndPinSelection(rows: TacticalDimensionRow[]): TacticalDimensionRow[] {
		const query = (this.searchTerm ?? "").trim().toLowerCase();
		const selected: TacticalDimensionRow[] = [];
		const rest: TacticalDimensionRow[] = [];

		rows.forEach((row) => {
			if (this.isSelected(row)) {
				selected.push(row);
				return;
			}

			if (query && !this.rowMatchesSearch(row, query)) {
				return;
			}

			rest.push(row);
		});

		return selected.length ? [...selected, ...rest] : rest;
	}

	private rowMatchesSearch(row: TacticalDimensionRow, query: string): boolean {
		const name = String(row?.cells?.name ?? "").toLowerCase();
		return name.includes(query);
	}

	private applyLockedOrder(
		source: TacticalDimensionRow[],
		orderKeys: string[]
	): TacticalDimensionRow[] {
		const byKey = new Map(source.map((row) => [row.key, row]));
		const ordered: TacticalDimensionRow[] = [];

		orderKeys.forEach((key) => {
			const row = byKey.get(key);
			if (row) {
				ordered.push(row);
				byKey.delete(key);
			}
		});

		source.forEach((row) => {
			if (byKey.has(row.key)) {
				ordered.push(row);
				byKey.delete(row.key);
			}
		});

		return ordered;
	}

	private compareCells(
		left: string | number | undefined,
		right: string | number | undefined
	): number {
		const leftNum = typeof left === "number" ? left : Number(left);
		const rightNum = typeof right === "number" ? right : Number(right);
		const bothNumeric =
			Number.isFinite(leftNum) &&
			Number.isFinite(rightNum) &&
			(typeof left === "number" ||
				typeof right === "number" ||
				(/^-?\d+(\.\d+)?$/.test(String(left ?? "").trim()) &&
					/^-?\d+(\.\d+)?$/.test(String(right ?? "").trim())));

		if (bothNumeric) {
			return leftNum - rightNum;
		}

		return String(left ?? "")
			.trim()
			.localeCompare(String(right ?? "").trim(), "pt-BR", {
				sensitivity: "base",
				numeric: true,
			});
	}

	private splitKeyIds(key: string): string[] {
		return (key ?? "")
			.split("|")
			.map((part) => part.trim())
			.filter((part) => !!part);
	}

	private scheduleScrollObserverRefresh(): void {
		if (this.scrollObserverRefreshScheduled) {
			return;
		}
		this.scrollObserverRefreshScheduled = true;
		this.scheduleTimeout(() => {
			this.scrollObserverRefreshScheduled = false;
			this.refreshScrollObserver();
		}, 0);
	}

	private refreshScrollObserver(): void {
		this.disconnectScrollObserver();
		if (!this.hasMoreRows || this.loading) {
			this.scrollObserverSetupAttempts = 0;
			return;
		}

		const root = this.scrollHost?.nativeElement ?? null;
		const sentinel = this.scrollSentinel?.nativeElement ?? null;
		if (!root || !sentinel) {
			if (this.scrollObserverSetupAttempts < SCROLL_OBSERVER_MAX_SETUP_ATTEMPTS) {
				this.scrollObserverSetupAttempts++;
				this.scheduleTimeout(() => this.refreshScrollObserver(), 80);
			}
			return;
		}
		this.scrollObserverSetupAttempts = 0;

		this.scrollObserver = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					this.emitLoadMore();
				}
			},
			{ root, rootMargin: "80px 0px", threshold: 0 }
		);
		this.scrollObserver.observe(sentinel);
	}

	private emitLoadMore(): void {
		if (!this.hasMoreRows || this.loadMoreInProgress) {
			return;
		}
		this.loadMoreInProgress = true;
		this.loadMore.emit();
		this.scheduleTimeout(() => {
			this.loadMoreInProgress = false;
			this.scheduleScrollObserverRefresh();
		}, 50);
	}

	private disconnectScrollObserver(): void {
		this.scrollObserver?.disconnect();
		this.scrollObserver = undefined;
	}

	private scheduleTimeout(callback: () => void, delayMs: number): void {
		const timeoutId = setTimeout(() => {
			this.removePendingTimeout(timeoutId);
			callback();
		}, delayMs);
		this.pendingTimeoutIds.push(timeoutId);
	}

	private clearPendingTimeouts(): void {
		this.pendingTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
		this.pendingTimeoutIds.length = 0;
	}

	private removePendingTimeout(timeoutId: ReturnType<typeof setTimeout>): void {
		const index = this.pendingTimeoutIds.indexOf(timeoutId);
		if (index >= 0) {
			this.pendingTimeoutIds.splice(index, 1);
		}
	}
}
